import React from 'react';
import type { ApiResponse, ErrorResponse } from '@/api/handlers/responses';

/** The branch the dialog is asking about. */
export type ArchiveBranchTarget = {
    id: string;
    /** Shown in the prompt so the user can see which branch goes out of service. */
    name: string;
};

type UseArchiveBranchConfirmParams = {
    /** Fires the archive. Resolves to the envelope so a 409 can be read off it. */
    onArchive: (id: string) => Promise<ApiResponse<unknown>>;
};

/**
 * Drives the branch-archive confirmation.
 *
 * Archiving used to be a single menu click. It is no longer that small: the
 * branch stops trading and its terminal API token is revoked (bos-api
 * BOS-124), so a terminal has to be enrolled again afterwards — and the server
 * refuses outright while a shift is open on the branch. Both of those are
 * things the user has to be told *before* the click, and the refusal is a
 * sentence with a remedy in it, so it is shown inline in the dialog rather
 * than as a toast that leaves before it is read.
 */
export const useArchiveBranchConfirm = ({
    onArchive,
}: UseArchiveBranchConfirmParams) => {
    const [target, setTarget] = React.useState<ArchiveBranchTarget | null>(
        null,
    );
    const [error, setError] = React.useState<ErrorResponse | null>(null);
    const [isPending, setIsPending] = React.useState(false);

    const onRequestArchive = React.useCallback((next: ArchiveBranchTarget) => {
        setTarget(next);
        setError(null);
    }, []);

    const onCancel = React.useCallback(() => {
        setTarget(null);
        setError(null);
        setIsPending(false);
    }, []);

    const onConfirm = React.useCallback(async () => {
        if (target === null) {
            return;
        }
        setIsPending(true);
        setError(null);
        try {
            const result = await onArchive(target.id);
            if (result.success) {
                setTarget(null);
                return;
            }
            setError(result.error);
        } finally {
            setIsPending(false);
        }
    }, [onArchive, target]);

    return { target, error, isPending, onCancel, onConfirm, onRequestArchive };
};
